/**
 * Auto-loading spinners for HTMX requests
 *
 * Buttons with an <i> icon + text label: swaps the icon to a spinner.
 * Icon-only buttons (no text): falls back to .btn--loading (full overlay).
 * Explicit opt-in: add data-icon-loading to any element.
 * Opt-out: add data-no-auto-loading to skip auto behavior.
 *
 * Status dropdowns: swaps the dot for a spinner while saving.
 * Forms: finds the submit button and applies the appropriate loading state.
 * Dropdown items: dims and disables during request.
 *
 * Native form submits (non-HTMX): listens for the submit event,
 * applies the same button loading state, and disables to prevent
 * double-submits. No cleanup needed — page navigates on success.
 */

(function () {

    function _startIconLoading(el) {
        var icon = el.querySelector('i');
        if (icon) {
            icon.setAttribute('data-original-class', icon.className);
            icon.className = 'fa-solid fa-spinner fa-spin';
        }
        el.disabled = true;
    }

    function _stopIconLoading(el) {
        var icon = el.querySelector('i');
        if (icon && icon.hasAttribute('data-original-class')) {
            icon.className = icon.getAttribute('data-original-class');
            icon.removeAttribute('data-original-class');
        }
        el.disabled = false;
    }

    function _hasIconAndLabel(el) {
        return el.querySelector('i') && el.textContent.trim();
    }

    function _startBtnLoading(btn) {
        if (_hasIconAndLabel(btn)) {
            _startIconLoading(btn);
            btn.setAttribute('data-icon-loading-auto', '');
        } else {
            btn.classList.add('btn--loading');
            btn.disabled = true;
        }
    }

    function _stopBtnLoading(btn) {
        if (btn.hasAttribute('data-icon-loading-auto')) {
            _stopIconLoading(btn);
            btn.removeAttribute('data-icon-loading-auto');
        } else if (btn.classList.contains('btn--loading')) {
            btn.classList.remove('btn--loading');
            btn.disabled = false;
        }
    }

    // Capture the actual submitter button (in capture phase, before HTMX intercepts)
    // so htmx:beforeRequest can target the clicked button instead of the form's first submit.
    document.body.addEventListener('submit', function (e) {
        if (e.target.tagName === 'FORM' && e.submitter) {
            e.target.__lastSubmitter = e.submitter;
        }
    }, true);

    document.body.addEventListener('htmx:beforeRequest', function (e) {
        var el = e.detail.elt;
        // data-icon-loading: explicit opt-in to icon swap
        if (el.hasAttribute('data-icon-loading')) {
            _startIconLoading(el);
            return;
        }
        if (el.dataset.noAutoLoading) return;
        // Forms: find the submit button and add loading state.
        // Checked before status-dropdown so forms inside dropdowns (e.g. reason
        // picker) spin the visible submit button, not the dot hidden behind the
        // open panel.
        if (el.tagName === 'FORM') {
            var submitBtn = el.__lastSubmitter ||
                            el.querySelector('button[type="submit"]') ||
                            el.querySelector('button:not([type="button"]):not([type="reset"])');
            if (!submitBtn && el.id) {
                submitBtn = document.querySelector('button[form="' + el.id + '"]');
            }
            if (submitBtn && !submitBtn.dataset.noAutoLoading) {
                _startBtnLoading(submitBtn);
            }
            return;
        }
        // Status dropdown: swap dot for spinner while saving
        var statusDropdown = el.closest('.status-dropdown');
        if (statusDropdown) {
            var dot = statusDropdown.querySelector('.status-dropdown-toggle .status-dropdown-dot');
            if (dot) {
                dot.setAttribute('data-original-bg', dot.style.background || '');
                dot.style.background = 'none';
                dot.style.border = '2px solid rgba(232,232,240,0.25)';
                dot.style.borderTopColor = 'rgba(232,232,240,0.8)';
                dot.style.animation = 'spin 0.6s linear infinite';
            }
            return;
        }
        // Dropdown items: dim + prevent double-click
        if (el.classList.contains('dropdown-item')) {
            el.style.opacity = '0.5';
            el.style.pointerEvents = 'none';
            return;
        }
        // Action buttons (any btn-* class with HTMX)
        if ((el.tagName === 'BUTTON' || el.tagName === 'A') && /\bbtn-/.test(el.className)) {
            _startBtnLoading(el);
        }
    });

    // ── Native form submit (non-HTMX) ──
    document.body.addEventListener('submit', function (e) {
        var form = e.target;
        if (form.tagName !== 'FORM') return;
        // Skip HTMX-powered forms — htmx:beforeRequest handles those
        if (form.hasAttribute('hx-post') || form.hasAttribute('hx-put') ||
            form.hasAttribute('hx-patch') || form.hasAttribute('hx-delete') ||
            form.hasAttribute('hx-get') ||
            form.hasAttribute('data-hx-post') || form.hasAttribute('data-hx-put') ||
            form.hasAttribute('data-hx-patch') || form.hasAttribute('data-hx-delete') ||
            form.hasAttribute('data-hx-get')) return;
        var submitBtn = e.submitter ||
                        form.querySelector('button[type="submit"]') ||
                        form.querySelector('button:not([type="button"]):not([type="reset"])');
        if (!submitBtn && form.id) {
            submitBtn = document.querySelector('button[form="' + form.id + '"]');
        }
        if (submitBtn && !submitBtn.dataset.noAutoLoading) {
            _startBtnLoading(submitBtn);
        }
    });

    document.body.addEventListener('htmx:afterRequest', function (e) {
        var el = e.detail.elt;
        // Restore icon-loading buttons (explicit data-icon-loading)
        if (el.hasAttribute('data-icon-loading')) {
            _stopIconLoading(el);
        }
        // Forms: clean up submit button loading state
        if (el.tagName === 'FORM') {
            var submitBtn = el.querySelector('.btn--loading, [data-icon-loading-auto]');
            if (!submitBtn && el.id) {
                submitBtn = document.querySelector('button[form="' + el.id + '"].btn--loading, button[form="' + el.id + '"][data-icon-loading-auto]');
            }
            if (submitBtn) {
                _stopBtnLoading(submitBtn);
            }
            el.__lastSubmitter = null;
            return;
        }
        // Restore status dropdown dot
        var statusDropdown = el.closest('.status-dropdown');
        if (statusDropdown) {
            var dot = statusDropdown.querySelector('.status-dropdown-toggle .status-dropdown-dot');
            if (dot && dot.hasAttribute('data-original-bg')) {
                dot.style.background = dot.getAttribute('data-original-bg') || '';
                dot.removeAttribute('data-original-bg');
                dot.style.border = '';
                dot.style.borderTopColor = '';
                dot.style.animation = '';
            }
            return;
        }
        // Dropdown items: restore
        if (el.classList.contains('dropdown-item')) {
            el.style.opacity = '';
            el.style.pointerEvents = '';
            return;
        }
        // Standard buttons: restore
        _stopBtnLoading(el);
    });

})();
